import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { GIT_PROVIDERS } from '../../consts/git';
import { useNavigateWithQueryParams } from '../../hooks/useNavigateWithQueryParams';
import { useWizardAnalytics } from '../../hooks/useWizardAnalytics';
import {
  BitbucketWizardSelectionOption,
  BitbucketWebhookResult,
  BitbucketWorkspaceInfo,
} from '../../network/httpClients/RepositoryClient/api.schema';
import { QUERY_KEYS } from '../../network/queries/consts';
import { useBitbucketConfigQuery } from '../../network/queries/useBitbucketConfigQuery';
import { ROUTES } from '../../routes/consts';
import { MultiStepDialog } from '../../shared/MultiStepDialog';
import { spawn } from '../../utils/translations';
import { BitbucketInstallationContext } from './bitbucketInstallationContext';
import { getSteps } from './configuration';
import { Title } from './parts/Title';

const spawnGeneral = spawn('dialogs.bitbucket-installation.general');

export const BitbucketInstallationDialog = () => {
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [selectedWorkspace, setSelectedWorkspace] = useState<
    BitbucketWorkspaceInfo | undefined
  >();
  const [bitbucketWorkspaceSlug, setBitbucketWorkspaceSlug] = useState<
    string | undefined
  >();
  const [repositories, setRepositories] = useState<
    BitbucketWizardSelectionOption[]
  >([]);
  const [selectedProviderRepoIds, setSelectedProviderRepoIds] = useState<
    string[]
  >([]);
  const [webhookResults, setWebhookResults] = useState<
    BitbucketWebhookResult[]
  >([]);

  const { navigateWithQueryParams } = useNavigateWithQueryParams();
  const queryClient = useQueryClient();
  const { data: config } = useBitbucketConfigQuery();

  const callbackUrl = config?.callbackUrl ?? '';
  const oauthPopupBaseUrl = config?.oauthPopupBaseUrl ?? '';
  const oauthApiBaseUrl = config?.oauthApiBaseUrl ?? '';
  const oauthClientId = config?.oauthClientId ?? '';
  const oauthScopes = config?.oauthScopes ?? '';

  const steps = getSteps();
  const {
    onStepViewed,
    onStepNavigated,
    onCancel: trackClose,
    onWizardCompleted,
  } = useWizardAnalytics({
    gitProvider: GIT_PROVIDERS.BITBUCKET,
    wizardVersion: 'v1',
    steps,
  });

  const close = () => {
    navigateWithQueryParams(ROUTES.INTEGRATIONS);
  };

  const onCancel = (stepIndex: number, stepId: string) => {
    trackClose(stepIndex, stepId);
    close();
  };

  const onComplete = () => {
    onWizardCompleted();
    close();
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.INSTALLATIONS_COUNTS],
    });
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.GIT_INSTALLATIONS],
    });
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.GIT_REPOSITORIES],
    });
  };

  return (
    <BitbucketInstallationContext.Provider
      value={{
        callbackUrl,
        oauthPopupBaseUrl,
        oauthApiBaseUrl,
        oauthClientId,
        oauthScopes,
        sessionId,
        setSessionId,
        selectedWorkspace,
        setSelectedWorkspace,
        bitbucketWorkspaceSlug,
        setBitbucketWorkspaceSlug,
        repositories,
        setRepositories,
        selectedProviderRepoIds,
        setSelectedProviderRepoIds,
        webhookResults,
        setWebhookResults,
      }}
    >
      <MultiStepDialog
        title={<Title text={spawnGeneral('title')} />}
        steps={steps}
        onCancel={onCancel}
        onComplete={onComplete}
        onStepViewed={onStepViewed}
        onStepNavigated={onStepNavigated}
        className="sm:max-w-250!"
        disableOutsideClick={true}
        disableEscapeKey={true}
      />
    </BitbucketInstallationContext.Provider>
  );
};
